import React, { useEffect, useRef, forwardRef, ForwardedRef } from 'react';

export interface AutoResizeTextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  /**
   * Additional class name for the textarea
   */
  className?: string;
  /**
   * Value for controlled component
   */
  value?: string;
  /**
   * Default value for uncontrolled component
   */
  defaultValue?: string;
  /**
   * Callback when value changes
   */
  onChange?: React.ChangeEventHandler<HTMLTextAreaElement>;
  /**
   * Minimum height of the textarea in pixels
   */
  minHeight?: number;
}

/**
 * A textarea component that automatically resizes its height based on content
 */
export const AutoResizeTextarea = forwardRef(
  (
    {
      className = '',
      value,
      defaultValue,
      onChange,
      minHeight = 38,
      style,
      onInput,
      ...props
    }: AutoResizeTextareaProps,
    ref: ForwardedRef<HTMLTextAreaElement>,
  ) => {
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);

    // Function to adjust textarea height
    const adjustHeight = () => {
      const textarea = textareaRef.current;
      if (textarea) {
        textarea.style.height = 'auto';
        textarea.style.height = `${textarea.scrollHeight}px`;
      }
    };

    // Combine refs (forwarded ref and internal ref)
    const setRefs = (element: HTMLTextAreaElement | null) => {
      textareaRef.current = element;

      // Handle forwarded ref
      if (typeof ref === 'function') {
        ref(element);
      } else if (ref) {
        // Using a local variable to avoid direct assignment to function parameter property
        const currentRef = ref;
        currentRef.current = element;
      }
    };

    // Adjust height when value changes or on mount
    useEffect(() => {
      adjustHeight();
    }, [value, defaultValue]);

    // Handle input event
    const handleInput = (e: React.FormEvent<HTMLTextAreaElement>) => {
      adjustHeight();

      // Call the original onInput if provided
      if (onInput) {
        onInput(e);
      }
    };

    // Handle onChange event
    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      adjustHeight();

      // Call the original onChange if provided
      if (onChange) {
        onChange(e);
      }
    };

    return (
      <textarea
        ref={setRefs}
        className={className}
        value={value}
        defaultValue={defaultValue}
        onChange={handleChange}
        onInput={handleInput}
        rows={1}
        style={{
          resize: 'none',
          overflow: 'hidden',
          minHeight: `${minHeight}px`,
          height: 'auto',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          ...style,
        }}
        data-testid="auto-resize-textarea"
        {...props}
      />
    );
  },
);

AutoResizeTextarea.displayName = 'AutoResizeTextarea';
